import * as React from "react"
import { useContext, useState } from "react"
import AppBar from "@mui/material/AppBar"
import Box from "@mui/material/Box"
import Toolbar from "@mui/material/Toolbar"
import Typography from "@mui/material/Typography"
import { Container, IconButton, Menu, MenuItem, Tooltip } from "@mui/material"
import {
  Link as RouterLink,
  useMatch,
  useNavigate,
  useResolvedPath,
} from "react-router-dom"
import { AuthContext } from "../contexts/auth-context"
import { useTranslation } from "react-i18next"
import MenuIcon from "@mui/icons-material/Menu"
import { UserContext } from "../contexts/user-context"
import { BackgroundLetterAvatar } from "./ui/background-letter-avatar"
import LanguageSelect from "./language-select"

const User = () => {
  const { t } = useTranslation(["common"])
  const navigate = useNavigate()
  const { logout } = useContext(AuthContext)
  const { user } = useContext(UserContext)
  const [anchorElUser, setAnchorElUser] = useState(null)
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  const handleLogout = async () => {
    setAnchorElUser(null)
    try {
      logout()
    } catch (error) { }
    navigate("/logout")
  }

  const handleProfile = async () => {
    setAnchorElUser(null)
    navigate("/profile")
  }

  return (
    <>
      {user && (
        <Tooltip title={t("menu")}>
          <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
            <BackgroundLetterAvatar
              name={user.name}
              id={user.id}
              sx={{ color: "#fff" }}
            />
          </IconButton>
        </Tooltip>
      )}
      <Menu
        sx={{ mt: "45px" }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        <MenuItem onClick={handleProfile}>
          <Typography textAlign="center">{t("profile")}</Typography>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Typography textAlign="center">{t("logout")}</Typography>
        </MenuItem>
      </Menu>
    </>
  )
}

export default function MainAppBar() {
  const { t } = useTranslation(["common"])
  const { isAuthenticated } = useContext(AuthContext)
  const { user } = useContext(UserContext)

  const MenuLink = ({ menuItem, onClick }) => {
    const { t } = useTranslation(["common"])
    const resolved = useResolvedPath(menuItem.path)
    const match = useMatch({ path: resolved.pathname, end: true })
    const navigate = useNavigate()

    const handleClick = () => {
      onClick?.()
      navigate(menuItem.path)
    }

    return (
      <MenuItem onClick={handleClick} disabled={!!match}>
        <Typography textAlign="center">{t(menuItem.name)}</Typography>
      </MenuItem>
    )
  }

  const [anchorElNav, setAnchorElNav] = React.useState(null)

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget)
  }

  const handleCloseNavMenu = () => {
    setAnchorElNav(null)
  }


  let menuItems = [
    { name: "projects", path: "/projects" },
    { name: "help", path: "/help" }
  ]
  if (user?.role === "admin")
    menuItems = [...menuItems, { name: "users", path: "/users" }]

  return (
    <AppBar position="fixed" component="nav" sx={{ zIndex: 1100 }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Box
            component={RouterLink}
            to="/"
            sx={{
              color: "#ffffff",
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src="/images/logo_96.png"
              sx={{ maxWidth: 46, mr: 2 }}
            />
            <Typography
              variant="h6"
              noWrap
              sx={{
                mr: 2,
                display: { xs: "none", md: "flex" },
                fontWeight: 700,
                color: "inherit",
                textDecoration: "none",
              }}
            >
              {t("appName")}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            {isAuthenticated && (
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
            )}
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: "block", md: "none" },
              }}
            >
              {menuItems.map((x) => (
                <MenuLink
                  key={x.path}
                  menuItem={x}
                  onClick={handleCloseNavMenu}
                />
              ))}
            </Menu>
          </Box>
          <Typography
            variant="h5"
            noWrap
            component="a"
            href=""
            sx={{
              mr: 2,
              display: { xs: "flex", md: "none" },
              flexGrow: 1,
              fontWeight: 700,
              color: "inherit",
              textDecoration: "none",
            }}
          >
            {t("appName")}
          </Typography>
          <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
            {isAuthenticated &&
              menuItems.map((x) => (
                <MenuLink
                  key={x.path}
                  menuItem={x}
                  onClick={handleCloseNavMenu}
                />
              ))}
          </Box>
          <Box sx={{ mr: 2 }}>
            <LanguageSelect />
          </Box>

          <Box sx={{ flexGrow: 0 }}>{isAuthenticated && <User />}</Box>
        </Toolbar>
      </Container>
    </AppBar>
  )
}
